import axios from 'axios';
import { defineStore } from 'pinia';
import { authStore } from '../stores/auth';

const transformWorkOrderMessage = (message) => ({
    ...message,
    fromTenant: message.messageType === 'from_tenant',
    authorDisplayName: message.messageType === 'from_tenant' ? 'Du' : message.author,
    createDate: new Date(message.createDate),
});

const transformWorkOrders = (workOrders) =>
    workOrders.map((workOrder) => ({
        ...workOrder,
        messages: workOrder.messages.map(transformWorkOrderMessage),
    }));

export const workordersStore = defineStore('workorders-store', {
    state: () => {
        return {
            allWorkorders: '',
            loadAll: false,
            options: null,
            maintenanceUnits: [],
        };
    },

    getters: {
        workorders(state) {
            return state.allWorkorders;
        },
        workorderOptions(state) {
            return state.options;
        },
        workorderMaintenanceUnits(state) {
            return state.maintenanceUnits;
        },
    },

    actions: {
        async fetchWorkorders() {
            const response = await axios.get(
                process.env.VUE_APP_API_BASE_URL + 'applicants/me/workorders',
                authStore().authConfig
            );
            try {
                this.allWorkorders = transformWorkOrders(response.data.workOrders);
            } catch (err) {
                this.allWorkorders = '';
                console.error('Error loading Workorders:', err);
                return err;
            }
        },

        async fetchAllWorkorders() {
            this.loadAll = true;
            const response = await axios.get(
                process.env.VUE_APP_API_BASE_URL + 'applicants/me/workorders?getAll=true',
                authStore().authConfig
            );
            try {
                this.allWorkorders = transformWorkOrders(response.data.workOrders);
                this.loadAll = false;
            } catch (err) {
                this.allWorkorders = '';
                console.error('Error loading Workorders:', err);
                return err;
            }
        },

        async fetchWorkorderOptions() {
            const response = await axios.get(
                process.env.VUE_APP_API_BASE_URL + 'applicants/me/workOrderOptions',
                authStore().authConfig
            );
            try {
                // this.options = response.data?.areas.length > 0 ? response.data.areas : workOrderOptions.areas;
                this.options = response.data.areas;
                this.maintenanceUnits = response.data.maintenanceUnits || [];
                if (!this.maintenanceUnits.some((unit) => unit.type === 'Tvättstuga')) {
                    this.options = this.options.map((area) =>
                        area.caption === 'Gemensamma utrymmen'
                            ? {
                                  ...area,
                                  locations: area.locations.filter((location) => location.caption !== 'Tvättstuga'),
                              }
                            : area
                    );
                }

                const apartmentArea = this.options.find((area) => area.caption === 'I lägenheten');
                if (apartmentArea) {
                    const kitchen = apartmentArea.locations.find((location) => location.code === 'KÖ');
                    if (kitchen) {
                        kitchen.partOfBuildings.push({
                            code: 'MU',
                            caption: 'Microvågsugn',
                        });
                    }
                }
            } catch (err) {
                this.options = [];
                console.error(err);
            }
        },

        async createWorkOrder(workOrder) {
            const {
                rentalObjectCode,
                images,
                email,
                phoneNumber,
                masterKey,
                rows,
                callBetween,
                pet,
                petText,
                hearingImpaired,
            } = workOrder;

            try {
                const response = await axios.post(
                    process.env.VUE_APP_API_BASE_URL + 'workorders/me',
                    {
                        rentalObjectCode,
                        images,
                        accessOptions: {
                            type: masterKey,
                            email,
                            phoneNumber,
                            callBetween: `${callBetween.from} - ${callBetween.to}`,
                        },
                        pet: pet ? petText : 'Nej',
                        hearingImpaired,
                        rows,
                    },
                    authStore().authConfig
                );

                if (response.status !== 200) {
                    console.error('Error creating work order:', response.status);
                    return false;
                }

                return (
                    (!response.data.hasOneCoreWorkOrders || response.data.oneCoreSuccess) &&
                    (!response.data.hasXpandWorkOrders || response.data.success)
                );
            } catch (err) {
                console.error('Error creating work order:', err);
                return false;
            }
        },

        async closeWorkOrder(workOrderId) {
            try {
                const response = await axios.post(
                    process.env.VUE_APP_API_BASE_URL + 'workorders/close/' + workOrderId,
                    {},
                    authStore().authConfig
                );

                return response.status === 204;
            } catch (err) {
                console.error('Error closing work order:', err);
                return false;
            }
        },

        async updateWorkOrder(workOrderId, message) {
            try {
                const response = await axios.post(
                    process.env.VUE_APP_API_BASE_URL + 'workorders/update/' + workOrderId,
                    {
                        message,
                    },
                    authStore().authConfig
                );

                return response.status === 204;
            } catch (err) {
                console.error('Error updating work order:', err);
                return false;
            }
        },
    },
});

// ------- THESE ARE NECESSARY FOR LOCAL DEVELOPMENT -------

// const workOrderOptions = {
//     areas: [
//         {
//             caption: 'I lägenheten',
//             locations: [
//                 {
//                     code: 'ABU',
//                     caption: 'Altan/Balkong/Uteplats',
//                     partOfBuildings: [
//                         {
//                             code: 'EL',
//                             caption: 'El',
//                         },
//                         {
//                             code: 'GRM',
//                             caption: 'Gräsmatta',
//                         },
//                         {
//                             code: 'IG',
//                             caption: 'Inglasning - Balkong, uteplats',
//                         },
//                         {
//                             code: 'ÖV',
//                             caption: 'Övrigt',
//                         },
//                         {
//                             code: 'STA',
//                             caption: 'Staket',
//                         },
//                         {
//                             code: 'ABD',
//                             caption: 'Altan eller balkongdörr',
//                         },
//                         {
//                             code: 'BEL',
//                             caption: 'Belysning',
//                         },
//                     ],
//                 },
//                 {
//                     code: 'BWC',
//                     caption: 'Badrum/WC',
//                     partOfBuildings: [
//                         {
//                             code: 'BEL',
//                             caption: 'Belysning',
//                         },
//                         {
//                             code: 'BLB',
//                             caption: 'Blandare, badkar',
//                         },
//                         {
//                             code: 'BLD',
//                             caption: 'Blandare, dusch',
//                         },
//                         {
//                             code: 'BLT',
//                             caption: 'Blandare, tvättställ',
//                         },
//                         {
//                             code: 'DUDS',
//                             caption: 'Duschdrapperistång',
//                         },
//                         {
//                             code: 'DUMS',
//                             caption: 'Duschmunstycke',
//                         },
//                         {
//                             code: 'DUSL',
//                             caption: 'Duschslang',
//                         },
//                         {
//                             code: 'GB',
//                             caption: 'Golvbrunn',
//                         },
//                         {
//                             code: 'HDT',
//                             caption: 'Handdukstork',
//                         },
//                         {
//                             code: 'ELE',
//                             caption: 'Element',
//                         },
//                         {
//                             code: 'SB',
//                             caption: 'Strömbrytare',
//                         },
//                         {
//                             code: 'EL',
//                             caption: 'El',
//                         },
//                         {
//                             code: 'BDS',
//                             caption: 'Badrumsskåp',
//                         },
//                         {
//                             code: 'TM',
//                             caption: 'Tvättmaskin',
//                         },
//                         {
//                             code: 'TO',
//                             caption: 'Toalettstol',
//                         },
//                         {
//                             code: 'TS',
//                             caption: 'Tvättställ',
//                         },
//                         {
//                             code: 'TT',
//                             caption: 'Torktumlare',
//                         },
//                         {
//                             code: 'VENT',
//                             caption: 'Ventilation ',
//                         },
//                         {
//                             code: 'ÖV ',
//                             caption: 'Övrigt ',
//                         },
//                         {
//                             code: 'VÄRM',
//                             caption: 'Värme ',
//                         },
//                     ],
//                 },
//                 {
//                     code: 'FÖR',
//                     caption: 'Förråd',
//                     partOfBuildings: [
//                         {
//                             code: 'EL',
//                             caption: 'El ',
//                         },
//                         {
//                             code: 'SKAD',
//                             caption: 'Skadegörelse',
//                         },
//                         {
//                             code: 'BEL',
//                             caption: 'Belysning',
//                         },
//                         {
//                             code: 'ÖV ',
//                             caption: 'Övrigt ',
//                         },
//                         {
//                             code: 'SD ',
//                             caption: 'Skadedjur ',
//                         },
//                     ],
//                 },
//                 {
//                     code: 'KÖ',
//                     caption: 'Kök',
//                     partOfBuildings: [
//                         {
//                             code: 'FÖ',
//                             caption: 'Fönster',
//                         },
//                         {
//                             code: 'GO',
//                             caption: 'Golv',
//                         },
//                         {
//                             code: 'BLK',
//                             caption: 'Blandare, kök',
//                         },
//                         {
//                             code: 'DB',
//                             caption: 'Diskbänk',
//                         },
//                         {
//                             code: 'ELU',
//                             caption: 'Eluttag',
//                         },
//                         {
//                             code: 'FL',
//                             caption: 'Fläkt',
//                         },
//                         {
//                             code: 'SB',
//                             caption: 'Strömbrytare',
//                         },
//                         {
//                             code: 'SP',
//                             caption: 'Spis/ugn',
//                         },
//                         {
//                             code: 'DM',
//                             caption: 'Diskmaskin',
//                         },
//                         {
//                             code: 'KY',
//                             caption: 'Kyl',
//                         },
//                         {
//                             code: 'FR',
//                             caption: 'Frys',
//                         },
//                         {
//                             code: 'KF',
//                             caption: 'Kyl/frys',
//                         },
//                         {
//                             code: 'ELE',
//                             caption: 'Element',
//                         },
//                         {
//                             code: 'VA',
//                             caption: 'Vatten',
//                         },
//                         {
//                             code: 'ÖV',
//                             caption: 'Övrigt',
//                         },
//                     ],
//                 },
//                 {
//                     code: 'HALL',
//                     caption: 'Hall',
//                     partOfBuildings: [
//                         {
//                             code: 'GA',
//                             caption: 'Garderob',
//                         },
//                         {
//                             code: 'BR',
//                             caption: 'Brandvarnare',
//                         },
//                         {
//                             code: 'HA',
//                             caption: 'Hatthylla',
//                         },
//                         {
//                             code: 'ELU',
//                             caption: 'Eluttag',
//                         },
//                         {
//                             code: 'ELE',
//                             caption: 'Element',
//                         },
//                         {
//                             code: 'ÖV',
//                             caption: 'Övrigt ',
//                         },
//                     ],
//                 },
//                 {
//                     code: 'VARD',
//                     caption: 'Vardagsrum',
//                     partOfBuildings: [
//                         {
//                             code: 'FÖ',
//                             caption: 'Fönster',
//                         },
//                         {
//                             code: 'GO',
//                             caption: 'Golv',
//                         },
//                         {
//                             code: 'ELE',
//                             caption: 'Element',
//                         },
//                         {
//                             code: 'TA',
//                             caption: 'Tak',
//                         },
//                         {
//                             code: 'SB',
//                             caption: 'Strömbrytare',
//                         },
//                         {
//                             code: 'ELU',
//                             caption: 'Eluttag',
//                         },
//                         {
//                             code: 'ÖV',
//                             caption: 'Övrigt ',
//                         },
//                     ],
//                 },
//                 {
//                     code: 'SOV',
//                     caption: 'Sovrum',
//                     partOfBuildings: [
//                         {
//                             code: 'GA',
//                             caption: 'Garderob',
//                         },
//                         {
//                             code: 'FÖ',
//                             caption: 'Fönster',
//                         },
//                         {
//                             code: 'GO',
//                             caption: 'Golv',
//                         },
//                         {
//                             code: 'ID',
//                             caption: 'Innerdörr',
//                         },
//                         {
//                             code: 'ELE',
//                             caption: 'Element',
//                         },
//                         {
//                             code: 'SB',
//                             caption: 'Strömbrytare',
//                         },
//                         {
//                             code: 'ELU',
//                             caption: 'Eluttag',
//                         },
//                         {
//                             code: 'ÖV',
//                             caption: 'Övrigt',
//                         },
//                     ],
//                 },
//                 {
//                     code: 'LGH ',
//                     caption: 'Hela lägenheten ',
//                     partOfBuildings: [
//                         {
//                             code: 'VÄ',
//                             caption: 'Värme ',
//                         },
//                         {
//                             code: 'SD ',
//                             caption: 'Skadedjur ',
//                         },
//                         {
//                             code: 'VENT',
//                             caption: 'Ventilation ',
//                         },
//                         {
//                             code: 'ÖV',
//                             caption: 'Övrigt ',
//                         },
//                     ],
//                 },
//             ],
//         },
//         {
//             caption: 'Bilplatskontrakt ',
//             locations: [
//                 {
//                     code: 'PP',
//                     caption: 'Parkeringsplats ',
//                     partOfBuildings: [
//                         {
//                             code: 'ÖV',
//                             caption: 'Övrigt ',
//                         },
//                         {
//                             code: 'MV',
//                             caption: 'Motorvärmaruttag',
//                         },
//                     ],
//                 },
//             ],
//         },
//         {
//             caption: 'Gemensamma utrymmen',
//             locations: [
//                 {
//                     code: 'MB',
//                     caption: 'Miljöbod',
//                     partOfBuildings: [
//                         {
//                             code: 'FÖ',
//                             caption: 'Fönster',
//                         },
//                         {
//                             code: 'BEL',
//                             caption: 'Belysning ',
//                         },
//                         {
//                             code: 'YD',
//                             caption: 'Ytterdörr',
//                         },
//                         {
//                             code: 'STÄD',
//                             caption: 'Städning',
//                         },
//                         {
//                             code: 'TÖM',
//                             caption: 'Tömning',
//                         },
//                     ],
//                 },
//                 {
//                     code: 'TV',
//                     caption: 'Tvättstuga',
//                     partOfBuildings: [
//                         {
//                             code: 'FÖ',
//                             caption: 'Fönster',
//                         },
//                         {
//                             code: 'GB',
//                             caption: 'Golvbrunn',
//                         },
//                         {
//                             code: 'MA',
//                             caption: 'Mangel',
//                         },
//                         {
//                             code: 'ÖV',
//                             caption: 'Övrigt',
//                         },
//                         {
//                             code: 'TÅ',
//                             caption: 'Torkskåp',
//                         },
//                         {
//                             code: 'TM',
//                             caption: 'Tvättmaskin',
//                         },
//                         {
//                             code: 'TT',
//                             caption: 'Torktumlare',
//                         },
//                         {
//                             code: 'BEL',
//                             caption: 'Belysning ',
//                         },
//                     ],
//                 },
//                 {
//                     code: 'TH',
//                     caption: 'Trapphus',
//                     partOfBuildings: [
//                         {
//                             code: 'STÄD',
//                             caption: 'Städning',
//                         },
//                         {
//                             code: 'BEL',
//                             caption: 'Belysning',
//                         },
//                         {
//                             code: 'ÖV',
//                             caption: 'Övrigt',
//                         },
//                         {
//                             code: 'YD',
//                             caption: 'Ytterdörr',
//                         },
//                     ],
//                 },
//                 {
//                     code: 'VI ',
//                     caption: 'Vind ',
//                     partOfBuildings: [
//                         {
//                             code: 'BEL',
//                             caption: 'Belysning',
//                         },
//                         {
//                             code: 'DÖR',
//                             caption: 'Dörr',
//                         },
//                         {
//                             code: 'ÖV',
//                             caption: 'Övrigt ',
//                         },
//                     ],
//                 },
//                 {
//                     code: 'KÄL',
//                     caption: 'Källare',
//                     partOfBuildings: [
//                         {
//                             code: 'BEL',
//                             caption: 'Belysning',
//                         },
//                         {
//                             code: 'DJUR',
//                             caption: 'Skadedjur',
//                         },
//                         {
//                             code: 'ÖV',
//                             caption: 'Övrigt ',
//                         },
//                         {
//                             code: 'SKAD',
//                             caption: 'Skadegörelse',
//                         },
//                         {
//                             code: 'STÄD',
//                             caption: 'Städning',
//                         },
//                     ],
//                 },
//                 {
//                     code: 'CYF',
//                     caption: 'Cykelförråd ',
//                     partOfBuildings: [
//                         {
//                             code: 'BEL',
//                             caption: 'Belysning',
//                         },
//                         {
//                             code: 'DÖR',
//                             caption: 'Dörr',
//                         },
//                         {
//                             code: 'ÖV',
//                             caption: 'Övrigt ',
//                         },
//                     ],
//                 },
//                 {
//                     code: 'ÖV',
//                     caption: 'Övrigt',
//                     partOfBuildings: [
//                         {
//                             code: 'ÖV',
//                             caption: 'Övrigt',
//                         },
//                     ],
//                 },
//                 {
//                     code: 'UTE',
//                     caption: 'Gården/utomhus',
//                     partOfBuildings: [
//                         {
//                             code: 'LEK',
//                             caption: 'Lekplats',
//                         },
//                         {
//                             code: 'GRM',
//                             caption: 'Gräsmatta',
//                         },
//                         {
//                             code: 'GM',
//                             caption: 'Gröna Mimer',
//                         },
//                         {
//                             code: 'SNÖ',
//                             caption: 'Snöröjning/sandning',
//                         },
//                         {
//                             code: 'ÖV',
//                             caption: 'Övrigt',
//                         },
//                     ],
//                 },
//             ],
//         },
//     ],
// };
